import React, { useState, useRef, useEffect } from "react";
import { pressReleaseApi } from "../../config/api";
import { apiClient, apiFormDataClient } from "../../utils/apiClient";
import { compressImage } from "../../utils/imageCompressor";
import "./PressRelease.scss";

const CATEGORIES = [
  "All", "Awards & Rankings", "Academic", "Events", "MoU & Partnerships",
  "Sports", "Cultural", "Infrastructure", "Other",
];

const CATEGORY_COLORS = {
  "Awards & Rankings": { bg: "#fefce8", color: "#854d0e", border: "#fde68a" },
  Academic: { bg: "#f0fdf4", color: "#166534", border: "#bbf7d0" },
  Events: { bg: "#fdf4ff", color: "#7c3aed", border: "#e9d5ff" },
  "MoU & Partnerships": { bg: "#eff6ff", color: "#1e40af", border: "#bfdbfe" },
  Sports: { bg: "#fff7ed", color: "#c2410c", border: "#fed7aa" },
  Cultural: { bg: "#fdf2f8", color: "#9d174d", border: "#fbcfe8" },
  Infrastructure: { bg: "#f0fdfa", color: "#065f46", border: "#99f6e4" },
  Other: { bg: "#f8fafc", color: "#475569", border: "#e2e8f0" },
};

const MEDIA_OUTLETS = [
  "The Hindu", "Times of India", "Deccan Herald", "New Indian Express",
  "Hindustan Times", "Indian Express", "Mathrubhumi", "Dinamalar",
  "Vijay Karnataka", "Prajavani", "TV9 Kannada", "Sun TV", "NDTV", "DD News", "Other"
];

const BLANK_FORM = {
  _id: null, Title: "", Category: "Awards & Rankings", Media_Outlet: "",
  Publish_Date: "", Headline: "", Content: "", Source_URL: "",
  Thumbnail_URL: "", Pin_to_Top: false, Visible_on_Website: true, Tags: [],
};

const PressRelease = () => {
  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeCategory, setActiveCategory] = useState("All");
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(""); // "add", "edit", "view", "delete"
  const [form, setForm] = useState(BLANK_FORM);
  const [confirmId, setConfirmId] = useState(null);
  const [dropdown, setDropdown] = useState({ id: null, top: 0, left: 0 });
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [viewItem, setViewItem] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => { fetchReleases(); }, []);

  const fetchReleases = async () => {
    try {
      setLoading(true);
      const response = await apiClient(pressReleaseApi);
      const result = await response.json();
      if (response.ok) { setReleases(result.data || []); }
    } catch (error) {
      console.error("Error fetching releases:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const onOut = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdown({ id: null, top: 0, left: 0 }); };
    const onScroll = () => setDropdown({ id: null, top: 0, left: 0 });
    document.addEventListener("mousedown", onOut);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("mousedown", onOut);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, []);

  const openDropdown = (e, id) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    let left = rect.right - 230;
    if (left < 8) left = 8;
    setDropdown({ id, top: rect.bottom + 8, left });
  };

  const openAdd = () => { setForm(BLANK_FORM); setSelectedFile(null); setPreviewUrl(null); setModal("add"); };
  const openEdit = (item) => { setForm({ ...item }); setSelectedFile(null); setPreviewUrl(null); setModal("edit"); setDropdown({ id: null, top: 0, left: 0 }); };
  const openView = (item) => { setViewItem(item); setModal("view"); };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const compressed = await compressImage(file);
      setSelectedFile(compressed);
      setPreviewUrl(URL.createObjectURL(compressed));
    }
  };

  const removePhoto = () => {
    setForm(p => ({ ...p, Thumbnail_URL: "" }));
    setSelectedFile(null);
    setPreviewUrl(null);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const method = modal === "add" ? "POST" : "PUT";
      const url = modal === "add" ? pressReleaseApi : `${pressReleaseApi}/${form._id}`;

      const formDataToSend = new FormData();
      Object.keys(form).forEach(key => {
        if (key !== 'Thumbnail_URL' && form[key] !== null && form[key] !== undefined) {
          formDataToSend.append(key, form[key]);
        }
      });

      if (selectedFile) {
        const compressed = await compressImage(selectedFile);
        formDataToSend.append('Thumbnail_URL', compressed);
      } else if (form.Thumbnail_URL) {
        formDataToSend.append('Thumbnail_URL', form.Thumbnail_URL);
      }

      const response = await apiFormDataClient(url, {
        method,
        body: formDataToSend,
      });

      if (response.ok) {
        fetchReleases();
        setModal("");
      } else {
        const errJson = await response.json().catch(() => ({}));
        alert(`Failed to save release: ${errJson.message || response.statusText || 'Server error'}`);
      }
    } catch (error) {
      console.error("Error saving release:", error);
      alert(`Error saving release: ${error.message || 'Network error'}`);
    } finally {
      setLoading(false);
    }
  };

  const deleteRelease = async () => {
    try {
      setLoading(true);
      const response = await apiClient(`${pressReleaseApi}/${confirmId}`, { method: "DELETE" });
      if (response.ok) { fetchReleases(); setConfirmId(null); setModal(""); }
    } catch (error) {
      console.error("Error deleting release:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateItem = async (id, patch) => {
    try {
      const item = releases.find(r => r._id === id);
      const formData = new FormData();
      const updatedData = { ...item, ...patch };

      Object.keys(updatedData).forEach(key => {
        if (updatedData[key] !== null) formData.append(key, updatedData[key]);
      });

      await apiFormDataClient(`${pressReleaseApi}/${id}`, {
        method: "PUT",
        body: formData,
      });
      fetchReleases();
      setDropdown({ id: null, top: 0, left: 0 });
    } catch (error) {
      console.error("Error updating release:", error);
    }
  };

  const filtered = releases.filter((r) => {
    const matchCat = activeCategory === "All" || r.Category === activeCategory;
    const matchSearch = !search || (r.Title || "").toLowerCase().includes(search.toLowerCase()) || (r.Media_Outlet || "").toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  const catColor = (cat) => CATEGORY_COLORS[cat] || CATEGORY_COLORS["Other"];

  const stats = {
    total: releases.length,
    pinned: releases.filter(r => r.Pin_to_Top).length,
    hidden: releases.filter(r => !r.Visible_on_Website).length,
    categories: [...new Set(releases.map(r => r.Category))].length
  };

  return (
    <div className="pr-page">
      <div className="pr-header">
        <div className="pr-header-left">
          <div className="pr-header-icon">📰</div>
          <div>
            <h1>Press Release</h1>
            <p>Manage official press announcements and news coverage for Rajat Group Of College.</p>
          </div>
        </div>
        <div className="pr-header-actions">
          <button className="pr-btn-primary" onClick={openAdd}>+ Add Press Release</button>
        </div>
      </div>

      <div className="pr-stats-row">
        <div className="pr-stat-card">
          <div className="pr-stat-icon">📄</div>
          <div><strong>{stats.total}</strong><p>Total Releases</p></div>
        </div>
        <div className="pr-stat-card" style={{ borderTopColor: "#f59e0b" }}>
          <div className="pr-stat-icon">📌</div>
          <div><strong>{stats.pinned}</strong><p>Pinned Items</p></div>
        </div>
        <div className="pr-stat-card" style={{ borderTopColor: "#64748b" }}>
          <div className="pr-stat-icon">👁️</div>
          <div><strong>{stats.hidden}</strong><p>Hidden Items</p></div>
        </div>
        <div className="pr-stat-card" style={{ borderTopColor: "#10b981" }}>
          <div className="pr-stat-icon">🏷️</div>
          <div><strong>{stats.categories}</strong><p>Active Categories</p></div>
        </div>
      </div>

      <div className="pr-filters">
        <div className="pr-filters-top">
          <div className="pr-search">
            <input type="text" placeholder="Search by title or media outlet..." value={search} onChange={(e) => setSearch(e.target.value)} />
            {search && <button className="pr-clr" onClick={() => setSearch("")}>×</button>}
          </div>
          <div className="pr-cat-tabs">
            {CATEGORIES.map((cat) => (
              <button key={cat} className={`pr-cat-tab ${activeCategory === cat ? "active" : ""}`} onClick={() => setActiveCategory(cat)}>
                {cat}
                <span className="pr-cat-count">{cat === "All" ? releases.length : releases.filter(r => r.Category === cat).length}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading && <div className="loading-overlay">Loading...</div>}

      <div className="pr-grid">
        {filtered.length > 0 ? filtered.map((item) => {
          const cc = catColor(item.Category);
          const publishDate = item.Publish_Date ? new Date(item.Publish_Date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A';
          return (
            <div className={`pr-card ${!item.Visible_on_Website ? "hidden-card" : ""} ${item.Pin_to_Top ? "pinned-card" : ""}`} key={item._id}>
              <div className="pr-card-cover" onClick={() => openView(item)}>
                {item.Thumbnail_URL ? <img src={item.Thumbnail_URL} alt={item.Title} /> : <div className="pr-card-cover-placeholder">📰</div>}
                <div className="pr-card-cover-overlay"><span>Click to View Detail</span></div>
                <div className="pr-card-badges">
                  {item.Pin_to_Top && <span className="pr-badge pin">📌 Pinned</span>}
                  {!item.Visible_on_Website && <span className="pr-badge hidden">🔒 Hidden</span>}
                </div>
              </div>
              <div className="pr-card-body">
                <div className="pr-card-top">
                  <span className="pr-cat-pill" style={{ background: cc.bg, color: cc.color, border: `1px solid ${cc.border}` }}>{item.Category}</span>
                  <button className="pr-dots" onClick={(e) => openDropdown(e, item._id)}>⋮</button>
                </div>
                <h3 onClick={() => openView(item)}>{item.Title}</h3>
                <div className="pr-card-meta">
                  <span>🏢 {item.Media_Outlet}</span>
                  <span>📅 {publishDate}</span>
                </div>
                {item.Headline && <p className="pr-card-headline">{item.Headline}</p>}
                <div className="pr-card-footer" style={{ marginTop: 'auto', paddingTop: '12px', display: 'flex', gap: '8px' }}>
                  <button className="pr-btn-secondary" style={{ flex: 1, padding: '7px' }} onClick={() => openEdit(item)}>Edit</button>
                  <button className="pr-btn-secondary pr-dd-del" style={{ flex: 1, padding: '7px' }} onClick={() => { setConfirmId(item._id); setModal("delete"); }}>Delete</button>
                </div>
              </div>
            </div>
          );
        }) : (
          <div className="pr-empty" style={{ gridColumn: '1/-1' }}>
            <div>📭</div>
            <h3>No Press Releases Found</h3>
            <p>Try adjusting your filters or add a new release to get started.</p>
          </div>
        )}
      </div>

      {dropdown.id !== null && (
        <div ref={dropdownRef} className="pr-dropdown" style={{ position: "fixed", top: dropdown.top, left: dropdown.left, zIndex: 99999 }}>
          {(() => {
            const item = releases.find(x => x._id === dropdown.id);
            if (!item) return null;
            return (<>
              <button onClick={() => updateItem(item._id, { Pin_to_Top: !item.Pin_to_Top })}>
                {item.Pin_to_Top ? "📍 Unpin from Top" : "📌 Pin to Top"}
              </button>
              <button onClick={() => updateItem(item._id, { Visible_on_Website: !item.Visible_on_Website })}>
                {item.Visible_on_Website ? "👁️ Hide from Website" : "🌐 Show on Website"}
              </button>
              <div className="pr-dd-divider"></div>
              <button className="pr-dd-del" onClick={() => { setConfirmId(item._id); setModal("delete"); setDropdown({ id: null, top: 0, left: 0 }); }}>
                🗑️ Delete Permanent
              </button>
            </>);
          })()}
        </div>
      )}

      {(modal === "add" || modal === "edit") && (
        <div className="pr-overlay" onClick={() => setModal("")}>
          <div className="pr-form-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pr-modal-head">
              <div>
                <h2>{modal === "add" ? "Add New Press Release" : "Edit Press Release"}</h2>
                <p>Fill in the details to publish news coverage on the college website.</p>
              </div>
              <button className="pr-modal-close" onClick={() => setModal("")}>×</button>
            </div>
            <form className="pr-form" onSubmit={save}>
              <div className="pr-field full">
                <label>Title <span className="req">*</span></label>
                <input type="text" required placeholder="Enter the main title of the release" value={form.Title} onChange={(e) => setForm({ ...form, Title: e.target.value })} />
              </div>
              <div className="pr-field-row">
                <div className="pr-field">
                  <label>Category <span className="req">*</span></label>
                  <select value={form.Category} onChange={(e) => setForm({ ...form, Category: e.target.value })}>
                    {CATEGORIES.filter(c => c !== "All").map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="pr-field">
                  <label>Media Outlet <span className="req">*</span></label>
                  <select value={form.Media_Outlet} onChange={(e) => setForm({ ...form, Media_Outlet: e.target.value })}>
                    <option value="" disabled>Select Media Outlet</option>
                    {MEDIA_OUTLETS.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
              </div>
              <div className="pr-field-row">
                <div className="pr-field">
                  <label>Publish Date <span className="req">*</span></label>
                  <input type="date" required value={form.Publish_Date ? form.Publish_Date.split('T')[0] : ""} onChange={(e) => setForm({ ...form, Publish_Date: e.target.value })} />
                </div>
                <div className="pr-field">
                  <label>Source URL</label>
                  <input type="url" placeholder="https://..." value={form.Source_URL} onChange={(e) => setForm({ ...form, Source_URL: e.target.value })} />
                </div>
              </div>
              <div className="pr-field full">
                <label>Headline / Summary</label>
                <input type="text" placeholder="Brief catchy headline" value={form.Headline} onChange={(e) => setForm({ ...form, Headline: e.target.value })} />
              </div>

              <div className="pr-field full">
                <label>Thumbnail Image</label>
                <div className="pr-photo-upload">
                  {(previewUrl || form.Thumbnail_URL) ? (
                    <div className="pr-photo-preview">
                      <img src={previewUrl || form.Thumbnail_URL} alt="Preview" />
                      <div className="pr-photo-actions">
                        <label className="pr-change-btn">Change Image<input type="file" accept="image/*" onChange={handleFileChange} style={{ display: 'none' }} /></label>
                        <button type="button" className="pr-remove-btn" onClick={removePhoto}>Remove</button>
                      </div>
                    </div>
                  ) : (
                    <label className="pr-upload-placeholder">
                      <div className="pr-upload-icon">📷</div>
                      <span>Click to upload press thumbnail</span>
                      <input type="file" accept="image/*" onChange={handleFileChange} style={{ display: 'none' }} />
                    </label>
                  )}
                </div>
              </div>

              <div className="pr-field full">
                <label>Tags (Comma separated)</label>
                <div className="pr-tags-input-wrap">
                  {Array.isArray(form.Tags) ? form.Tags.map((tag, i) => (
                    <span key={i} className="pr-tag-chip">{tag}<button type="button" onClick={() => setForm({ ...form, Tags: form.Tags.filter((_, idx) => idx !== i) })}>×</button></span>
                  )) : (form.Tags || "").split(',').filter(t => t.trim()).map((tag, i) => (
                    <span key={i} className="pr-tag-chip">{tag}<button type="button" onClick={() => {
                      const tags = form.Tags.split(',').filter((_, idx) => idx !== i);
                      setForm({ ...form, Tags: tags.join(',') });
                    }}>×</button></span>
                  ))}
                  <input
                    type="text"
                    placeholder="Press enter to add tags..."
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const val = e.target.value.trim();
                        if (val) {
                          const currentTags = Array.isArray(form.Tags) ? form.Tags : (form.Tags ? form.Tags.split(',') : []);
                          if (!currentTags.includes(val)) {
                            setForm({ ...form, Tags: [...currentTags, val] });
                          }
                          e.target.value = "";
                        }
                      }
                    }}
                  />
                </div>
              </div>

              <div className="pr-field full">
                <label>Content / Description</label>
                <textarea rows="5" placeholder="Enter detailed content here..." value={form.Content} onChange={(e) => setForm({ ...form, Content: e.target.value })} />
              </div>

              <div className="pr-toggle-row">
                <label className="pr-toggle-item">
                  <input type="checkbox" checked={form.Pin_to_Top} onChange={(e) => setForm({ ...form, Pin_to_Top: e.target.checked })} />
                  <div className={`pr-chk-box ${form.Pin_to_Top ? 'checked' : ''}`}></div>
                  <div><strong>Pin to Top</strong><p>Featured items will stay at the top of the list.</p></div>
                </label>
                <label className="pr-toggle-item">
                  <input type="checkbox" checked={form.Visible_on_Website} onChange={(e) => setForm({ ...form, Visible_on_Website: e.target.checked })} />
                  <div className={`pr-chk-box ${form.Visible_on_Website ? 'checked' : ''}`}></div>
                  <div><strong>Visible on Website</strong><p>Toggle visibility for the public college website.</p></div>
                </label>
              </div>

              <div className="pr-form-footer">
                <button type="button" className="pr-btn-light" onClick={() => setModal("")}>Cancel</button>
                <button type="submit" className="pr-btn-primary">Save Release</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {modal === "view" && viewItem && (
        <div className="pr-overlay" onClick={() => setModal("")}>
          <div className="pr-view-modal" onClick={(e) => e.stopPropagation()}>
            <button className="pr-modal-close" onClick={() => setModal("")}>×</button>
            <div className="pr-view-cover">
              {viewItem.Thumbnail_URL ? <img src={viewItem.Thumbnail_URL} alt={viewItem.Title} /> : <div className="pr-card-cover-placeholder" style={{ height: '300px' }}>📰</div>}
              {viewItem.Pin_to_Top && <div className="pr-view-pin-badge">📌 Pinned</div>}
              {!viewItem.Visible_on_Website && <div className="pr-view-hidden-badge">🔒 Hidden</div>}
            </div>
            <div className="pr-view-body">
              <div className="pr-view-meta-row">
                <span className="pr-cat-pill" style={{ background: catColor(viewItem.Category).bg, color: catColor(viewItem.Category).color }}>{viewItem.Category}</span>
                <span className="pr-view-outlet">🏢 {viewItem.Media_Outlet}</span>
                <span className="pr-view-date">📅 {new Date(viewItem.Publish_Date).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
              </div>
              <h2>{viewItem.Title}</h2>
              {viewItem.Headline && <p className="pr-view-headline">{viewItem.Headline}</p>}
              <div className="pr-view-content">{viewItem.Content || "No detailed content available."}</div>
              {viewItem.Tags && viewItem.Tags.length > 0 && (
                <div className="pr-view-tags">
                  {viewItem.Tags.map((tag, i) => <span key={i} className="pr-tag">{tag}</span>)}
                </div>
              )}
              {viewItem.Source_URL && <a href={viewItem.Source_URL} target="_blank" rel="noreferrer" className="pr-source-link">Read Full Article External Link 🔗</a>}
              <div className="pr-view-actions">
                <button className="pr-btn-primary" onClick={() => openEdit(viewItem)}>Edit Content</button>
                <button className="pr-btn-secondary" onClick={() => setModal("")}>Close Preview</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {modal === "delete" && (
        <div className="pr-overlay" onClick={() => setModal("")}>
          <div className="pr-confirm" onClick={(e) => e.stopPropagation()}>
            <div className="pr-confirm-icon">⚠️</div>
            <h3>Delete Press Release?</h3>
            <p>This action cannot be undone. This release will be permanently removed from the system.</p>
            <div className="pr-confirm-actions">
              <button className="pr-btn-light" onClick={() => setModal("")}>Cancel</button>
              <button className="pr-btn-danger" onClick={deleteRelease}>Delete Permanent</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PressRelease;